
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from "recharts";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguage } from "@/components/LanguageContext";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface ProgressChartsProps {
  assessmentData: any[];
  trainingSessionsData: any[];
  progressData: any[];
  strengthData?: any[]; 
}

export const ProgressCharts = ({ assessmentData, trainingSessionsData, progressData, strengthData }: ProgressChartsProps) => {
  const { t } = useTranslations("progress");
  const { language } = useLanguage();
  const dateLocale = language === 'pt' ? ptBR : undefined;

  const formatDate = (date: string) => {
    return format(new Date(date), "dd/MM/yyyy", { locale: dateLocale });
  };

  const renderEmptyState = (title: string) => (
    <div className="flex items-center justify-center h-[300px]">
      <p className="text-muted-foreground dark:text-gray-400">
        {t("studentProgress.charts.noData")}
      </p>
    </div>
  );

  const customTooltip = (props: any) => {
    const { active, payload, label } = props;

    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border p-2 rounded-lg shadow-lg">
          <p className="text-sm text-muted-foreground">{formatDate(label)}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm">
              {entry.name}: {entry.value}
              {entry.name.includes("peso") || entry.name.includes("Weight") ? " kg" : 
               entry.name.includes("serie") || entry.name.includes("Sets") ? "" : "%"}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const getSessionColor = (source: string) => {
    return source === "trainer" ? "#DA5E29" : "#8884d8";
  };

  console.log("Chart data:", { progressData, assessmentData, trainingSessionsData, strengthData });

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card className="dark:border-gray-700 dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="dark:text-white">{t("studentProgress.charts.progress")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            {progressData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={progressData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#474747" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#9CA3AF" 
                    tick={{ fill: "#E5E7EB" }}
                    tickFormatter={formatDate}
                  />
                  <YAxis stroke="#9CA3AF" tick={{ fill: "#E5E7EB" }} />
                  <Tooltip content={customTooltip} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#DA5E29"
                    name={t("studentProgress.charts.progress")}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : renderEmptyState(t("studentProgress.charts.progress"))}
          </div>
        </CardContent>
      </Card>

      <Card className="dark:border-gray-700 dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="dark:text-white">{t("studentProgress.charts.weightEvolution")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            {assessmentData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={assessmentData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#474747" />
                  <XAxis dataKey="date" stroke="#9CA3AF" tick={{ fill: "#E5E7EB" }} />
                  <YAxis stroke="#9CA3AF" tick={{ fill: "#E5E7EB" }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(31, 41, 55, 0.9)', 
                      border: '1px solid #374151',
                      color: '#E5E7EB'
                    }} 
                  />
                  <Line
                    type="monotone"
                    dataKey="peso"
                    stroke="#8884d8"
                    name={t("studentProgress.charts.weight")}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : renderEmptyState(t("studentProgress.charts.weightEvolution"))}
          </div>
        </CardContent>
      </Card>

      <Card className="dark:border-gray-700 dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="dark:text-white">{t("studentProgress.charts.bodyComposition")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            {assessmentData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={assessmentData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#474747" />
                  <XAxis dataKey="date" stroke="#9CA3AF" tick={{ fill: "#E5E7EB" }} />
                  <YAxis stroke="#9CA3AF" tick={{ fill: "#E5E7EB" }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(31, 41, 55, 0.9)', 
                      border: '1px solid #374151',
                      color: '#E5E7EB'
                    }} 
                  />
                  <Line
                    type="monotone"
                    dataKey="gordura"
                    stroke="#82ca9d"
                    name={t("studentProgress.charts.fatPercentage")}
                  />
                  <Line
                    type="monotone"
                    dataKey="muscular"
                    stroke="#ffc658"
                    name={t("studentProgress.charts.muscleMass")}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : renderEmptyState(t("studentProgress.charts.bodyComposition"))}
          </div>
        </CardContent>
      </Card>

      <Card className="dark:border-gray-700 dark:bg-gray-800">
        <CardHeader className="flex flex-row justify-between items-center">
          <CardTitle className="dark:text-white">{t("studentProgress.charts.strengthProgress")}</CardTitle>
          <div className="flex items-center text-xs space-x-4">
            <div className="flex items-center">
              <span className="w-3 h-3 inline-block bg-[#ff7300] mr-1 rounded-full"></span>
              <span className="dark:text-gray-300">{t("studentProgress.charts.trainerRecorded")}</span>
            </div>
            <div className="flex items-center">
              <span className="w-3 h-3 inline-block bg-[#82ca9d] mr-1 rounded-full"></span>
              <span className="dark:text-gray-300">{t("studentProgress.charts.studentRecorded")}</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            {strengthData && strengthData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={strengthData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#474747" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#9CA3AF" 
                    tick={{ fill: "#E5E7EB" }}
                    tickFormatter={formatDate} 
                  />
                  <YAxis stroke="#9CA3AF" tick={{ fill: "#E5E7EB" }} />
                  <Tooltip content={customTooltip} />
                  <Line
                    type="monotone"
                    dataKey="maxWeight"
                    stroke="#ff7300"
                    name={t("studentProgress.charts.maxWeight")}
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="totalWeight"
                    stroke="#82ca9d"
                    name={t("studentProgress.charts.totalWeight")}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : renderEmptyState(t("studentProgress.charts.strengthProgress"))}
          </div>
        </CardContent>
      </Card>

      <Card className="dark:border-gray-700 dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="dark:text-white">{t("studentProgress.charts.trainingVolume")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            {strengthData && strengthData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={strengthData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#474747" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#9CA3AF" 
                    tick={{ fill: "#E5E7EB" }}
                    tickFormatter={formatDate}
                  />
                  <YAxis stroke="#9CA3AF" tick={{ fill: "#E5E7EB" }} />
                  <Tooltip content={customTooltip} />
                  <Bar
                    dataKey="sets"
                    fill="#8884d8"
                    name={t("studentProgress.charts.totalSets")}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : renderEmptyState(t("studentProgress.charts.trainingVolume"))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
